import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _to_property_key } from "@swc/helpers/_/_to_property_key";
import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { AiFilterChip } from '~/components/AiSearchChips/AiFilterChip/AiFilterChip';
import { currentValue, nextPredicate } from '~/components/AiSearchChips/StringMatchChip/StringMatchChip.utils';
import { billedUponToTrigger, triggerToBilledUpon } from './agreementBillingTrigger';
import { agreementDateValueLabel, isExactMatch } from './AgreementDateChip/AgreementDateChip.utils';
import { lastBilledModeOf, lastBilledSummary } from './agreementLastBilled';
import { billedUponIsTriggerShaped, occurrenceToSet, setToOccurrenceFields } from './agreementOccurrence';
import { AgreementOrGroup } from './AgreementOrGroup/AgreementOrGroup';
import { hasServiceNameField, serviceNamesToSet, setToServiceNameArms } from './agreementServiceName';
const SERVICE_QUANTIFIERS = [
    'any',
    'all',
    'none'
];
export const activeServiceQuantifier = (services)=>SERVICE_QUANTIFIERS.find((quantifier)=>services[quantifier] != null);
export const activeBundleQuantifier = (bundles)=>SERVICE_QUANTIFIERS.find((quantifier)=>bundles[quantifier] != null);
export const setToCheckboxes = (predicate)=>{
    var _predicate_in;
    return predicate.nin != null ? {
        selected: predicate.nin,
        operator: 'exclude'
    } : {
        selected: (_predicate_in = predicate.in) !== null && _predicate_in !== void 0 ? _predicate_in : [],
        operator: 'include'
    };
};
export const checkboxesToSet = (next)=>next.operator === 'exclude' ? {
        nin: [
            ...next.selected
        ]
    } : {
        in: [
            ...next.selected
        ]
    };
const swapSetPolarity = (predicate)=>{
    const swapped = {};
    if (predicate.nin != null) swapped.in = predicate.nin;
    if (predicate.in != null) swapped.nin = predicate.in;
    return swapped;
};
export const toChipSetPredicate = (predicate, quantifier)=>quantifier === 'none' ? swapSetPolarity(predicate) : predicate;
export const fromChipSetPredicate = (predicate, quantifier)=>quantifier === 'none' ? swapSetPolarity(predicate) : predicate;
export const rebuildServiceCollection = (param)=>{
    let { services, quantifier, field, value } = param;
    var _services_quantifier;
    const _ref = (_services_quantifier = services[quantifier]) !== null && _services_quantifier !== void 0 ? _services_quantifier : {}, { [field]: _omit } = _ref, rest = _object_without_properties(_ref, [
        _to_property_key(field)
    ]);
    const nextQuantifier = value == null ? rest : _object_spread_props(_object_spread({}, rest), {
        [field]: value
    });
    if (Object.keys(nextQuantifier).length === 0) return undefined;
    return {
        [quantifier]: nextQuantifier
    };
};
export const rebuildServiceNames = (param)=>{
    let { services, quantifier, next } = param;
    var _services_quantifier;
    const _ref = (_services_quantifier = services[quantifier]) !== null && _services_quantifier !== void 0 ? _services_quantifier : {}, { name: _omitName, or } = _ref, rest = _object_without_properties(_ref, [
        "name",
        "or"
    ]);
    const nonNameArms = (or !== null && or !== void 0 ? or : []).filter((arm)=>arm.name == null);
    if (next == null) {
        const kept = nonNameArms.length > 0 ? _object_spread_props(_object_spread({}, rest), {
            or: nonNameArms
        }) : rest;
        return Object.keys(kept).length === 0 ? undefined : {
            [quantifier]: kept
        };
    }
    return {
        [quantifier]: _object_spread_props(_object_spread({}, rest), {
            or: [
                ...nonNameArms,
                ...setToServiceNameArms(next)
            ]
        })
    };
};
export const rebuildOccurrence = (param)=>{
    let { services, quantifier, next } = param;
    var _services_quantifier;
    const _ref = (_services_quantifier = services[quantifier]) !== null && _services_quantifier !== void 0 ? _services_quantifier : {}, { recurrence: _omitRecurrence, billedUpon: currentBilledUpon } = _ref, rest = _object_without_properties(_ref, [
        "recurrence",
        "billedUpon"
    ]);
    const fields = next == null ? {} : setToOccurrenceFields(next);
    const preservedBilledUpon = fields.billedUpon == null && currentBilledUpon != null && billedUponIsTriggerShaped(currentBilledUpon) ? {
        billedUpon: currentBilledUpon
    } : {};
    const nextQuantifier = _object_spread({}, rest, preservedBilledUpon, fields);
    if (Object.keys(nextQuantifier).length === 0) return undefined;
    return {
        [quantifier]: nextQuantifier
    };
};
export const rebuildBundleName = (param)=>{
    let { bundles, quantifier, value } = param;
    var _bundles_quantifier;
    const _ref = (_bundles_quantifier = bundles[quantifier]) !== null && _bundles_quantifier !== void 0 ? _bundles_quantifier : {}, { name: _omit } = _ref, rest = _object_without_properties(_ref, [
        "name"
    ]);
    const nextQuantifier = value == null ? rest : _object_spread_props(_object_spread({}, rest), {
        name: value
    });
    if (Object.keys(nextQuantifier).length === 0) return undefined;
    return {
        [quantifier]: nextQuantifier
    };
};
export const buildPredicateCallbacks = (predicate, onChange)=>{
    const setField = (key, value)=>{
        if (value == null) {
            const { [key]: _omit } = predicate, rest = _object_without_properties(predicate, [
                _to_property_key(key)
            ]);
            onChange(rest);
        } else {
            onChange(_object_spread_props(_object_spread({}, predicate), {
                [key]: value
            }));
        }
    };
    return {
        onStatusChange: (value)=>setField('status', value),
        onPaymentMethodChange: (value)=>setField('paymentMethod', value),
        onAmendmentStatusChange: (value)=>setField('amendmentStatus', value),
        onHasCreditChange: (value)=>setField('hasCredit', value),
        onAutomaticPriceIncreaseChange: (value)=>setField('automaticPriceIncrease', value),
        onEffectiveDateChange: (value)=>setField('effectiveDate', value),
        onApprovalDateChange: (value)=>setField('approvalDate', value),
        onClientNameChange: (value)=>setField('clientName', value),
        onAgreementTitleChange: (value)=>setField('agreementTitle', value),
        onContactNameChange: (value)=>setField('contactName', value),
        onAssigneeNameChange: (value)=>setField('assigneeName', value),
        onLastBilledChange: (value)=>setField('lastBilled', value),
        onServicesChange: (value)=>setField('services', value),
        onBundlesChange: (value)=>setField('bundles', value),
        onOrChange: (value)=>setField('or', value)
    };
};
const buildServiceChipUnits = (param)=>{
    let { services, testIds, callbacks, texts, autoOpenKey } = param;
    var _services_quantifier;
    const quantifier = activeServiceQuantifier(services);
    if (quantifier == null) return [];
    const service = (_services_quantifier = services[quantifier]) !== null && _services_quantifier !== void 0 ? _services_quantifier : {};
    const billedUpon = service.billedUpon;
    const billedUponIsTrigger = billedUpon != null && billedUponIsTriggerShaped(billedUpon);
    const hasOccurrence = service.recurrence != null || billedUpon != null && !billedUponIsTrigger;
    const checkboxTexts = {
        all: texts.setValue.all,
        count: texts.setValue.count,
        selectAll: texts.selectAllText,
        apply: texts.applyText,
        is: texts.isText(),
        isNot: texts.isNotText()
    };
    const units = [];
    if (hasOccurrence) {
        const occurrenceOptions = billedUponIsTrigger ? texts.options.occurrence.map((option)=>option.value === 'oneTime' || option.value === 'onDemand' ? _object_spread_props(_object_spread({}, option), {
                disabled: true
            }) : option) : texts.options.occurrence;
        const { selected, operator } = setToCheckboxes(toChipSetPredicate(occurrenceToSet(service), quantifier));
        units.push({
            key: 'service-recurrence',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.Occurrence,
                fieldLabel: texts.labels.occurrence,
                items: occurrenceOptions,
                selected: selected,
                operator: operator,
                onApply: (next)=>callbacks.onServicesChange(rebuildOccurrence({
                        services,
                        quantifier,
                        next: fromChipSetPredicate(checkboxesToSet(next), quantifier)
                    })),
                onRemove: ()=>callbacks.onServicesChange(rebuildOccurrence({
                        services,
                        quantifier,
                        next: undefined
                    })),
                autoOpen: autoOpenKey === 'service-recurrence',
                biPrefix: "agreements_ai_chip_service_occurrence",
                texts: checkboxTexts
            })
        });
    }
    if (service.automaticState != null) {
        const { selected, operator } = setToCheckboxes(toChipSetPredicate(service.automaticState, quantifier));
        units.push({
            key: 'service-automatic-state',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.AutomaticServices,
                fieldLabel: texts.labels.automaticServices,
                items: texts.options.automaticState,
                selected: selected,
                operator: operator,
                verb: "none",
                onApply: (next)=>callbacks.onServicesChange(rebuildServiceCollection({
                        services,
                        quantifier,
                        field: 'automaticState',
                        value: fromChipSetPredicate(checkboxesToSet(next), quantifier)
                    })),
                onRemove: ()=>callbacks.onServicesChange(rebuildServiceCollection({
                        services,
                        quantifier,
                        field: 'automaticState',
                        value: undefined
                    })),
                autoOpen: autoOpenKey === 'service-automatic-state',
                biPrefix: "agreements_ai_chip_service_automatic_state",
                texts: checkboxTexts
            })
        });
    }
    if (hasServiceNameField(service)) {
        const { selected, operator } = setToCheckboxes(serviceNamesToSet(service));
        units.push({
            key: 'service-name',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.ServiceName,
                fieldLabel: texts.labels.serviceName,
                items: texts.options.serviceName,
                selected: selected,
                operator: operator,
                verb: "is",
                searchable: true,
                onApply: (next)=>callbacks.onServicesChange(rebuildServiceNames({
                        services,
                        quantifier,
                        next: checkboxesToSet(next)
                    })),
                onRemove: ()=>callbacks.onServicesChange(rebuildServiceNames({
                        services,
                        quantifier,
                        next: undefined
                    })),
                autoOpen: autoOpenKey === 'service-name',
                biPrefix: "agreements_ai_chip_service_name",
                texts: checkboxTexts
            })
        });
    }
    if (billedUpon != null && billedUponIsTrigger) {
        const { selected, operator } = setToCheckboxes(toChipSetPredicate(billedUponToTrigger(billedUpon), quantifier));
        units.push({
            key: 'service-billing-trigger',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.BillingTrigger,
                fieldLabel: texts.labels.billingTrigger,
                items: texts.options.billingTrigger,
                selected: selected,
                operator: operator,
                onApply: (next)=>callbacks.onServicesChange(rebuildServiceCollection({
                        services,
                        quantifier,
                        field: 'billedUpon',
                        value: triggerToBilledUpon(fromChipSetPredicate(checkboxesToSet(next), quantifier))
                    })),
                onRemove: ()=>callbacks.onServicesChange(rebuildServiceCollection({
                        services,
                        quantifier,
                        field: 'billedUpon',
                        value: undefined
                    })),
                autoOpen: autoOpenKey === 'service-billing-trigger',
                biPrefix: "agreements_ai_chip_service_billing_trigger",
                texts: checkboxTexts
            })
        });
    }
    if (service.completionState != null) {
        const { selected, operator } = setToCheckboxes(toChipSetPredicate(service.completionState, quantifier));
        units.push({
            key: 'service-status',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.ServiceStatus,
                fieldLabel: texts.labels.serviceStatus,
                items: texts.options.completionState,
                selected: selected,
                operator: operator,
                onApply: (next)=>callbacks.onServicesChange(rebuildServiceCollection({
                        services,
                        quantifier,
                        field: 'completionState',
                        value: fromChipSetPredicate(checkboxesToSet(next), quantifier)
                    })),
                onRemove: ()=>callbacks.onServicesChange(rebuildServiceCollection({
                        services,
                        quantifier,
                        field: 'completionState',
                        value: undefined
                    })),
                autoOpen: autoOpenKey === 'service-status',
                biPrefix: "agreements_ai_chip_service_status",
                texts: checkboxTexts
            })
        });
    }
    return units;
};
const buildBundleChipUnits = (param)=>{
    let { bundles, testIds, callbacks, texts } = param;
    var _bundles_quantifier;
    const quantifier = activeBundleQuantifier(bundles);
    if (quantifier == null) return [];
    const bundle = (_bundles_quantifier = bundles[quantifier]) !== null && _bundles_quantifier !== void 0 ? _bundles_quantifier : {};
    const units = [];
    if (bundle.name != null) {
        const bundleName = bundle.name;
        units.push({
            key: 'bundle-name',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "text",
                testId: testIds.BundleName,
                fieldLabel: texts.labels.bundleName,
                value: currentValue(bundleName),
                verbPrefix: quantifier === 'none' ? texts.isNotText() : texts.isText(),
                onApply: (value)=>callbacks.onBundlesChange(rebuildBundleName({
                        bundles,
                        quantifier,
                        value: value === '' ? undefined : nextPredicate(value, bundleName)
                    })),
                onRemove: ()=>callbacks.onBundlesChange(rebuildBundleName({
                        bundles,
                        quantifier,
                        value: undefined
                    })),
                biPrefix: "agreements_ai_chip_bundle_name",
                texts: {
                    apply: texts.applyText
                }
            })
        });
    }
    return units;
};
const predicateFieldOfChip = (chipKey)=>{
    if (chipKey.startsWith('service-')) return 'services';
    if (chipKey === 'bundle-name') return 'bundles';
    return chipKey;
};
export const buildAgreementPredicateChipUnits = (param)=>{
    let { predicate, testIds, callbacks, texts, autoOpenKey = null } = param;
    const checkboxTexts = {
        all: texts.setValue.all,
        count: texts.setValue.count,
        selectAll: texts.selectAllText,
        apply: texts.applyText,
        is: texts.isText(),
        isNot: texts.isNotText()
    };
    const radioTexts = {
        is: texts.isText(),
        apply: texts.applyText,
        all: texts.setValue.all
    };
    const units = [];
    if (predicate.status != null) {
        const { selected, operator } = setToCheckboxes(predicate.status);
        units.push({
            key: 'status',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.Status,
                fieldLabel: texts.labels.status,
                items: texts.options.status,
                selected: selected,
                operator: operator,
                onApply: (next)=>callbacks.onStatusChange(checkboxesToSet(next)),
                onRemove: ()=>callbacks.onStatusChange(undefined),
                autoOpen: autoOpenKey === 'status',
                biPrefix: "agreements_ai_chip_status",
                texts: checkboxTexts
            })
        });
    }
    if (predicate.paymentMethod != null) {
        const { selected, operator } = setToCheckboxes(predicate.paymentMethod);
        units.push({
            key: 'paymentMethod',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.PaymentMethod,
                fieldLabel: texts.labels.paymentMethod,
                items: texts.options.paymentMethod,
                selected: selected,
                operator: operator,
                onApply: (next)=>callbacks.onPaymentMethodChange(checkboxesToSet(next)),
                onRemove: ()=>callbacks.onPaymentMethodChange(undefined),
                autoOpen: autoOpenKey === 'paymentMethod',
                biPrefix: "agreements_ai_chip_payment_method",
                texts: checkboxTexts
            })
        });
    }
    if (predicate.amendmentStatus != null) {
        const { selected, operator } = setToCheckboxes(predicate.amendmentStatus);
        units.push({
            key: 'amendmentStatus',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.AmendmentStatus,
                fieldLabel: texts.labels.amendmentStatus,
                items: texts.options.amendmentStatus,
                selected: selected,
                operator: operator,
                verb: "none",
                onApply: (next)=>callbacks.onAmendmentStatusChange(checkboxesToSet(next)),
                onRemove: ()=>callbacks.onAmendmentStatusChange(undefined),
                autoOpen: autoOpenKey === 'amendmentStatus',
                biPrefix: "agreements_ai_chip_amendment_status",
                texts: checkboxTexts
            })
        });
    }
    if (predicate.hasCredit != null) {
        const { selected, operator } = setToCheckboxes(predicate.hasCredit);
        units.push({
            key: 'hasCredit',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.HasCredit,
                fieldLabel: texts.labels.hasCredit,
                items: texts.options.hasCredit,
                selected: selected,
                operator: operator,
                verb: "none",
                onApply: (next)=>callbacks.onHasCreditChange(checkboxesToSet(next)),
                onRemove: ()=>callbacks.onHasCreditChange(undefined),
                autoOpen: autoOpenKey === 'hasCredit',
                biPrefix: "agreements_ai_chip_has_credit",
                texts: checkboxTexts
            })
        });
    }
    if (predicate.automaticPriceIncrease != null) {
        const { selected, operator } = setToCheckboxes(predicate.automaticPriceIncrease);
        units.push({
            key: 'automaticPriceIncrease',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.PriceIncrease,
                fieldLabel: texts.labels.priceIncrease,
                items: texts.options.priceIncrease,
                selected: selected,
                operator: operator,
                verb: "is",
                onApply: (next)=>callbacks.onAutomaticPriceIncreaseChange(checkboxesToSet(next)),
                onRemove: ()=>callbacks.onAutomaticPriceIncreaseChange(undefined),
                autoOpen: autoOpenKey === 'automaticPriceIncrease',
                biPrefix: "agreements_ai_chip_price_increase",
                texts: checkboxTexts
            })
        });
    }
    if (predicate.effectiveDate != null) {
        const effectiveDate = predicate.effectiveDate;
        const exact = isExactMatch(effectiveDate);
        const isExclude = effectiveDate.neq != null;
        const isEmpty = effectiveDate.gte == null && effectiveDate.lte == null && effectiveDate.eq == null && effectiveDate.neq == null;
        units.push({
            key: 'effectiveDate',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.EffectiveDate,
                fieldLabel: texts.labels.effectiveDate,
                items: [
                    {
                        value: 'all',
                        label: texts.dateRange.allTime
                    },
                    {
                        value: 'range',
                        label: texts.dateRange.specificRange
                    }
                ],
                selected: effectiveDate.gte != null || effectiveDate.lte != null ? 'range' : 'all',
                valueLabel: isEmpty ? texts.setValue.all : agreementDateValueLabel(effectiveDate, texts.labels.effectiveDate),
                valuePrefix: exact || isEmpty ? undefined : texts.isText(),
                exactMatch: exact ? {
                    label: isExclude ? texts.isNotText() : texts.isText(),
                    onToggle: ()=>callbacks.onEffectiveDateChange(isExclude ? {
                            eq: effectiveDate.neq
                        } : {
                            neq: effectiveDate.eq
                        }),
                    biName: 'agreements_ai_chip_effective_date_operator'
                } : undefined,
                datePicker: {
                    rangeValue: 'range',
                    from: effectiveDate.gte,
                    to: effectiveDate.lte,
                    texts: {
                        from: texts.dateRange.from,
                        to: texts.dateRange.to
                    }
                },
                onApply: (param)=>{
                    let { selected, from, to } = param;
                    if (selected === 'range') {
                        const next = {};
                        if (from != null) next.gte = from;
                        if (to != null) next.lte = to;
                        callbacks.onEffectiveDateChange(next.gte == null && next.lte == null ? undefined : next);
                    } else callbacks.onEffectiveDateChange(undefined);
                },
                onRemove: ()=>callbacks.onEffectiveDateChange(undefined),
                autoOpen: autoOpenKey === 'effectiveDate',
                biPrefix: "agreements_ai_chip_effective_date",
                texts: {
                    apply: texts.applyText,
                    all: texts.setValue.all
                }
            })
        });
    }
    if (predicate.approvalDate != null) {
        const approvalDate = predicate.approvalDate;
        const exact = isExactMatch(approvalDate);
        const isExclude = approvalDate.neq != null;
        const isEmpty = approvalDate.gte == null && approvalDate.lte == null && approvalDate.eq == null && approvalDate.neq == null;
        units.push({
            key: 'approvalDate',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.ApprovalDate,
                fieldLabel: texts.labels.approvalDate,
                items: [
                    {
                        value: 'all',
                        label: texts.dateRange.allTime
                    },
                    {
                        value: 'range',
                        label: texts.dateRange.specificRange
                    }
                ],
                selected: approvalDate.gte != null || approvalDate.lte != null ? 'range' : 'all',
                valueLabel: isEmpty ? texts.setValue.all : agreementDateValueLabel(approvalDate, texts.labels.approvalDate),
                valuePrefix: exact || isEmpty ? undefined : texts.isText(),
                exactMatch: exact ? {
                    label: isExclude ? texts.isNotText() : texts.isText(),
                    onToggle: ()=>callbacks.onApprovalDateChange(isExclude ? {
                            eq: approvalDate.neq
                        } : {
                            neq: approvalDate.eq
                        }),
                    biName: 'agreements_ai_chip_approval_date_operator'
                } : undefined,
                datePicker: {
                    rangeValue: 'range',
                    from: approvalDate.gte,
                    to: approvalDate.lte,
                    texts: {
                        from: texts.dateRange.from,
                        to: texts.dateRange.to
                    }
                },
                onApply: (param)=>{
                    let { selected, from, to } = param;
                    if (selected === 'range') {
                        const next = {};
                        if (from != null) next.gte = from;
                        if (to != null) next.lte = to;
                        callbacks.onApprovalDateChange(next.gte == null && next.lte == null ? undefined : next);
                    } else callbacks.onApprovalDateChange(undefined);
                },
                onRemove: ()=>callbacks.onApprovalDateChange(undefined),
                biPrefix: "agreements_ai_chip_approval_date",
                texts: {
                    apply: texts.applyText,
                    all: texts.setValue.all
                }
            })
        });
    }
    if (predicate.clientName != null) {
        const clientName = predicate.clientName;
        const current = currentValue(clientName);
        units.push({
            key: 'clientName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.ClientName,
                fieldLabel: texts.labels.clientName,
                items: texts.options.clientName,
                selected: current !== '' ? current : null,
                onApply: (param)=>{
                    let { selected } = param;
                    if (selected == null) callbacks.onClientNameChange(undefined);
                    else if (selected === current) callbacks.onClientNameChange(clientName);
                    else callbacks.onClientNameChange({
                        exact: selected
                    });
                },
                onRemove: ()=>callbacks.onClientNameChange(undefined),
                biPrefix: "agreements_ai_chip_client_name",
                texts: radioTexts
            })
        });
    }
    if (predicate.agreementTitle != null) {
        const agreementTitle = predicate.agreementTitle;
        const current = currentValue(agreementTitle);
        units.push({
            key: 'agreementTitle',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.AgreementTitle,
                fieldLabel: texts.labels.agreementTitle,
                items: texts.options.agreementTitle,
                selected: current !== '' ? current : null,
                onApply: (param)=>{
                    let { selected } = param;
                    if (selected == null) callbacks.onAgreementTitleChange(undefined);
                    else if (selected === current) callbacks.onAgreementTitleChange(agreementTitle);
                    else callbacks.onAgreementTitleChange({
                        exact: selected
                    });
                },
                onRemove: ()=>callbacks.onAgreementTitleChange(undefined),
                biPrefix: "agreements_ai_chip_agreement_title",
                texts: radioTexts
            })
        });
    }
    if (predicate.contactName != null) {
        const contactName = predicate.contactName;
        const current = currentValue(contactName);
        units.push({
            key: 'contactName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.ContactName,
                fieldLabel: texts.labels.contactName,
                items: texts.options.contactName,
                selected: current !== '' ? current : null,
                onApply: (param)=>{
                    let { selected } = param;
                    if (selected == null) callbacks.onContactNameChange(undefined);
                    else if (selected === current) callbacks.onContactNameChange(contactName);
                    else callbacks.onContactNameChange({
                        exact: selected
                    });
                },
                onRemove: ()=>callbacks.onContactNameChange(undefined),
                biPrefix: "agreements_ai_chip_contact_name",
                texts: radioTexts
            })
        });
    }
    if (predicate.assigneeName != null) {
        const assigneeName = predicate.assigneeName;
        const current = currentValue(assigneeName);
        units.push({
            key: 'assigneeName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.Assignee,
                fieldLabel: texts.labels.assignee,
                items: texts.options.assignee,
                selected: current !== '' ? current : null,
                onApply: (param)=>{
                    let { selected } = param;
                    if (selected == null) callbacks.onAssigneeNameChange(undefined);
                    else if (selected === current) callbacks.onAssigneeNameChange(assigneeName);
                    else callbacks.onAssigneeNameChange({
                        exact: selected
                    });
                },
                onRemove: ()=>callbacks.onAssigneeNameChange(undefined),
                biPrefix: "agreements_ai_chip_assignee",
                texts: radioTexts
            })
        });
    }
    if (predicate.lastBilled != null) {
        const lastBilled = predicate.lastBilled;
        units.push({
            key: 'lastBilled',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.LastBilled,
                fieldLabel: texts.labels.lastBilled,
                items: [
                    {
                        value: 'all',
                        label: texts.lastBilled.all
                    },
                    {
                        value: 'ever',
                        label: texts.lastBilled.everBilled
                    },
                    {
                        value: 'never',
                        label: texts.lastBilled.neverBilled
                    },
                    {
                        value: 'range',
                        label: texts.lastBilled.specificRange
                    }
                ],
                selected: lastBilledModeOf(lastBilled),
                editable: lastBilled.eq == null && lastBilled.neq == null,
                valueLabel: lastBilledSummary(lastBilled, {
                    allChip: texts.lastBilled.allChip,
                    everBilled: texts.lastBilled.everBilled,
                    neverBilled: texts.lastBilled.neverBilled,
                    specificRange: texts.lastBilled.specificRange
                }),
                datePicker: {
                    rangeValue: 'range',
                    from: lastBilled.gte,
                    to: lastBilled.lte,
                    texts: {
                        from: texts.lastBilled.from,
                        to: texts.lastBilled.to
                    }
                },
                onApply: (param)=>{
                    let { selected, from, to } = param;
                    if (selected === 'ever') callbacks.onLastBilledChange({
                        exists: true
                    });
                    else if (selected === 'never') callbacks.onLastBilledChange({
                        exists: false
                    });
                    else if (selected === 'range') {
                        const next = {};
                        if (from != null) next.gte = from;
                        if (to != null) next.lte = to;
                        callbacks.onLastBilledChange(next.gte == null && next.lte == null ? undefined : next);
                    } else callbacks.onLastBilledChange(undefined);
                },
                onRemove: ()=>callbacks.onLastBilledChange(undefined),
                autoOpen: autoOpenKey === 'lastBilled',
                biPrefix: "agreements_ai_chip_last_billed",
                texts: {
                    apply: texts.applyText,
                    all: texts.lastBilled.all
                }
            })
        });
    }
    if (predicate.services != null) {
        units.push(...buildServiceChipUnits({
            services: predicate.services,
            testIds,
            callbacks,
            texts,
            autoOpenKey
        }));
    }
    if (predicate.bundles != null) {
        units.push(...buildBundleChipUnits({
            bundles: predicate.bundles,
            testIds,
            callbacks,
            texts
        }));
    }
    if (predicate.or != null) {
        units.push({
            key: 'or',
            node: /*#__PURE__*/ _jsx(AgreementOrGroup, {
                testId: testIds.OrGroup,
                arms: predicate.or,
                texts: texts,
                onChange: callbacks.onOrChange
            })
        });
    }
    const insertionOrder = Object.keys(predicate);
    const orderIndex = (chipKey)=>{
        if (chipKey === 'status') return -1;
        const index = insertionOrder.indexOf(predicateFieldOfChip(chipKey));
        return index === -1 ? insertionOrder.length : index;
    };
    return units.map((unit, index)=>({
            unit,
            index
        })).sort((a, b)=>orderIndex(a.unit.key) - orderIndex(b.unit.key) || a.index - b.index).map((entry)=>entry.unit);
};
